import dayjs from '@/lib/dayjs'
import {
  getPriceAdjustmentCardTitle,
  getPriceAdjustmentUpcomingStartTimestamp,
} from '@/lib/services/price-adjustments/priceAdjustments.summary'

export const COLUMN_STYLES = {
  active: {
    dot: 'bg-info',
  },
  scheduled: {
    dot: 'bg-warning',
  },
  unscheduled: {
    dot: 'bg-muted-foreground/55',
  },
}

export const adjustmentIsScheduledBoardItem = (adjustment) =>
  ['scheduled', 'draft'].includes(adjustment?.status) ||
  (adjustment?.status === 'disabled' &&
    adjustment?.when_type === 'scheduled' &&
    adjustment?.can_still_affect_future_pricing === true)

export const adjustmentIsUnscheduledBoardItem = (adjustment) =>
  adjustment?.status === 'expired' ||
  (adjustment?.status === 'disabled' && !adjustmentIsScheduledBoardItem(adjustment))

export const adjustmentIsVisibleOnBoard = (adjustment) =>
  adjustment?.status === 'active' ||
  adjustmentIsScheduledBoardItem(adjustment) ||
  adjustmentIsUnscheduledBoardItem(adjustment)

export const getRuleCountLabel = (count) => `${count} ${count === 1 ? 'Rule' : 'Rules'}`

export const getActiveWindowStartTimestamp = (adjustment) => {
  const startsAt = adjustment?.active_window?.starts_at
  const parsed = startsAt ? dayjs(startsAt) : null

  return parsed?.isValid() ? parsed.valueOf() : 0
}

export const getBoundaryTimestamp = (value) => {
  const parsed = value ? dayjs(value) : null

  return parsed?.isValid() ? parsed.valueOf() : null
}

export const getPriceAdjustmentCardDomId = (adjustmentId) => `price-change-card-${adjustmentId}`

export const getPriceAdjustmentPriorityValue = (adjustment) =>
  Number(adjustment?.priority ?? 0) || 0

export const priceAdjustmentIsPriority = (adjustment) =>
  getPriceAdjustmentPriorityValue(adjustment) > 0

export const comparePriceAdjustmentsByEffectivePriority = (left, right) => {
  const priorityDifference =
    getPriceAdjustmentPriorityValue(right) - getPriceAdjustmentPriorityValue(left)

  if (priorityDifference !== 0) {
    return priorityDifference
  }

  const timestampDifference =
    getActiveWindowStartTimestamp(right) - getActiveWindowStartTimestamp(left)

  if (timestampDifference !== 0) {
    return timestampDifference
  }

  return Number(right?.id ?? 0) - Number(left?.id ?? 0)
}

export const getLatestStartedAdjustment = (adjustments) =>
  [...adjustments].sort(comparePriceAdjustmentsByEffectivePriority)[0] ?? null

const adjustmentIsCollectionScoped = (adjustment) =>
  adjustment?.scope_type === 'collections' || (adjustment?.collections?.length ?? 0) > 0

const getActiveBoardPriority = (adjustment) => (adjustmentIsCollectionScoped(adjustment) ? 1 : 0)

export const sortActiveBoardAdjustments = (adjustments) =>
  [...adjustments].sort((left, right) => {
    const priorityDifference = getActiveBoardPriority(right) - getActiveBoardPriority(left)

    if (priorityDifference !== 0) {
      return priorityDifference
    }

    return comparePriceAdjustmentsByEffectivePriority(left, right)
  })

export const sortActiveBoardAdjustmentsByEffect = (adjustments, activeEffectMetaByAdjustmentId) =>
  sortActiveBoardAdjustments(adjustments).sort((left, right) => {
    const leftApplied = Number(
      activeEffectMetaByAdjustmentId?.[Number(left?.id)]?.tone === 'applies'
    )
    const rightApplied = Number(
      activeEffectMetaByAdjustmentId?.[Number(right?.id)]?.tone === 'applies'
    )

    return rightApplied - leftApplied
  })

const getActiveBoardEffectMeta = (isApplied) =>
  isApplied
    ? { label: 'Applies Now', tone: 'applies', variant: 'success' }
    : { label: 'Overridden', tone: 'overridden', variant: 'secondary' }

export const getActiveBoardEffectMetaByAdjustmentId = (adjustments) => {
  const activeAdjustments = adjustments.filter((adjustment) => adjustment?.status === 'active')
  const activeEffectMetaByAdjustmentId = {}

  activeAdjustments.forEach((adjustment) => {
    const adjustmentId = Number(adjustment?.id)

    if (adjustmentId > 0) {
      activeEffectMetaByAdjustmentId[adjustmentId] = getActiveBoardEffectMeta(false)
    }
  })

  const activeBaseAdjustments = activeAdjustments.filter(
    (adjustment) => !adjustmentIsCollectionScoped(adjustment)
  )
  const activeOverrideAdjustments = activeAdjustments.filter(adjustmentIsCollectionScoped)
  const activeBaseWinner = getLatestStartedAdjustment(activeBaseAdjustments)

  if (activeBaseWinner?.id) {
    activeEffectMetaByAdjustmentId[Number(activeBaseWinner.id)] = getActiveBoardEffectMeta(true)
  }

  getCurrentCollectionOverrideGroups(activeOverrideAdjustments).forEach(({ adjustment }) => {
    const adjustmentId = Number(adjustment?.id)

    if (adjustmentId > 0) {
      activeEffectMetaByAdjustmentId[adjustmentId] = getActiveBoardEffectMeta(true)
    }
  })

  return activeEffectMetaByAdjustmentId
}

export const getScheduledBoardSortTimestamp = (adjustment) => {
  return getPriceAdjustmentUpcomingStartTimestamp(adjustment)
}

export const sortScheduledBoardAdjustments = (adjustments) =>
  [...adjustments].sort((left, right) => {
    const leftSortTimestamp = getScheduledBoardSortTimestamp(left)
    const rightSortTimestamp = getScheduledBoardSortTimestamp(right)

    if (leftSortTimestamp !== rightSortTimestamp) {
      if (leftSortTimestamp === null) {
        return 1
      }

      if (rightSortTimestamp === null) {
        return -1
      }

      return leftSortTimestamp - rightSortTimestamp
    }

    const stateComparison =
      Number(left?.status === 'disabled') - Number(right?.status === 'disabled')

    if (stateComparison !== 0) {
      return stateComparison
    }

    const titleComparison = (getPriceAdjustmentCardTitle(left) || '').localeCompare(
      getPriceAdjustmentCardTitle(right) || ''
    )

    if (titleComparison !== 0) {
      return titleComparison
    }

    return Number(left?.id ?? 0) - Number(right?.id ?? 0)
  })

export const formatActiveWindowEndDate = (endsAt) => {
  if (!endsAt) {
    return null
  }

  const parsed = dayjs(endsAt)

  return parsed.isValid() ? parsed.subtract(1, 'day').format('MMM D') : null
}

export const formatBoundaryDate = (boundaryAt) => {
  if (!boundaryAt) {
    return null
  }

  const parsed = dayjs(boundaryAt)

  return parsed.isValid() ? parsed.format('MMM D') : null
}

export const getPriceAdjustmentNoticeLabel = (adjustment) => getPriceAdjustmentCardTitle(adjustment)

export const getPriceAdjustmentNoticeListLabel = (adjustments) => {
  const labels = adjustments.map(getPriceAdjustmentNoticeLabel).filter(Boolean)

  if (labels.length === 0) {
    return 'price changes'
  }

  if (labels.length === 1) {
    return labels[0]
  }

  if (labels.length === 2) {
    return `${labels[0]} and ${labels[1]}`
  }

  return `${labels.slice(0, 2).join(', ')}, and ${labels.length - 2} more`
}

export const getPriceAdjustmentRuleListLabel = (adjustments) => {
  const labels = adjustments
    .map(getPriceAdjustmentNoticeLabel)
    .filter(Boolean)
    .map((label) => `${label} rule`)

  if (labels.length === 0) {
    return 'the next price change'
  }

  if (labels.length === 1) {
    return `the ${labels[0]}`
  }

  if (labels.length === 2) {
    return `the ${labels[0]} and ${labels[1]}`
  }

  return `the ${labels.slice(0, 2).join(', ')}, and ${labels.length - 2} more`
}

export const getAdjustmentCollections = (adjustment) =>
  (adjustment?.collections ?? [])
    .map((collection) => ({
      id: Number(collection?.id),
      name: collection?.name,
    }))
    .filter((collection) => collection.id > 0 && collection.name)

export const getUniqueCollections = (collections) => {
  const uniqueCollections = new Map()

  collections.forEach((collection) => {
    const collectionId = Number(collection?.id)
    const collectionName = collection?.name

    if (collectionId > 0 && collectionName && !uniqueCollections.has(collectionId)) {
      uniqueCollections.set(collectionId, {
        id: collectionId,
        name: collectionName,
      })
    }
  })

  return [...uniqueCollections.values()]
}

export const getAdjustmentActiveAfterBoundary = (adjustments, endingAdjustment) => {
  const endingBoundaryTimestamp = getBoundaryTimestamp(endingAdjustment?.active_window?.ends_at)

  if (endingBoundaryTimestamp === null) {
    return null
  }

  return getLatestStartedAdjustment(
    adjustments.filter((adjustment) => {
      if (Number(adjustment?.id) === Number(endingAdjustment?.id)) {
        return false
      }

      const startsAtTimestamp = getActiveWindowStartTimestamp(adjustment)
      const endsAtTimestamp = getBoundaryTimestamp(adjustment?.active_window?.ends_at)

      return (
        startsAtTimestamp <= endingBoundaryTimestamp &&
        (endsAtTimestamp === null || endsAtTimestamp > endingBoundaryTimestamp)
      )
    })
  )
}

const adjustmentCoversCollectionId = (adjustment, collectionId) =>
  getAdjustmentCollections(adjustment).some(
    (collection) => Number(collection.id) === Number(collectionId)
  )

export const getFallbackAdjustmentForCollection = (
  collectionId,
  endingAdjustment,
  activeOverrideAdjustments,
  activeBaseAdjustments
) => {
  const nextCollectionAdjustment = getAdjustmentActiveAfterBoundary(
    activeOverrideAdjustments.filter((adjustment) =>
      adjustmentCoversCollectionId(adjustment, collectionId)
    ),
    endingAdjustment
  )

  return (
    nextCollectionAdjustment ??
    getAdjustmentActiveAfterBoundary(activeBaseAdjustments, endingAdjustment)
  )
}

export const getCurrentCollectionOverrideGroups = (activeOverrideAdjustments) => {
  const winnersByCollectionId = new Map()

  activeOverrideAdjustments.forEach((adjustment) => {
    getAdjustmentCollections(adjustment).forEach((collection) => {
      const currentWinner = winnersByCollectionId.get(collection.id)?.adjustment
      const winner = getLatestStartedAdjustment([currentWinner, adjustment].filter(Boolean))

      winnersByCollectionId.set(collection.id, {
        collection,
        adjustment: winner,
      })
    })
  })

  const groupsByAdjustmentId = new Map()

  winnersByCollectionId.forEach(({ collection, adjustment }) => {
    const adjustmentId = Number(adjustment?.id)

    if (!adjustmentId) {
      return
    }

    const group = groupsByAdjustmentId.get(adjustmentId) ?? {
      adjustment,
      collections: [],
    }

    group.collections.push(collection)
    groupsByAdjustmentId.set(adjustmentId, group)
  })

  return [...groupsByAdjustmentId.values()].sort((left, right) =>
    comparePriceAdjustmentsByEffectivePriority(left.adjustment, right.adjustment)
  )
}

export const getAdjustmentIds = (adjustments) =>
  adjustments.map((adjustment) => Number(adjustment?.id)).filter((adjustmentId) => adjustmentId > 0)

export const getNextEndingAdjustments = (adjustments) => {
  const sortedAdjustments = [...adjustments]
    .filter((adjustment) => adjustment?.active_window?.ends_at)
    .sort((left, right) => {
      const leftEndsAt = dayjs(left.active_window.ends_at)
      const rightEndsAt = dayjs(right.active_window.ends_at)

      return leftEndsAt.valueOf() - rightEndsAt.valueOf()
    })

  const nextEndingTimestamp = getBoundaryTimestamp(sortedAdjustments[0]?.active_window?.ends_at)

  if (nextEndingTimestamp === null) {
    return []
  }

  return sortedAdjustments.filter((adjustment) => {
    return getBoundaryTimestamp(adjustment?.active_window?.ends_at) === nextEndingTimestamp
  })
}

export const getResumingBaseAdjustment = (activeBaseAdjustments, endingBaseAdjustment) => {
  const endingBoundaryTimestamp = getBoundaryTimestamp(endingBaseAdjustment?.active_window?.ends_at)

  if (endingBoundaryTimestamp === null) {
    return null
  }

  return getLatestStartedAdjustment(
    activeBaseAdjustments.filter((adjustment) => {
      if (Number(adjustment?.id) === Number(endingBaseAdjustment?.id)) {
        return false
      }

      const startsAtTimestamp = getActiveWindowStartTimestamp(adjustment)
      const endsAtTimestamp = getBoundaryTimestamp(adjustment?.active_window?.ends_at)

      return (
        startsAtTimestamp <= endingBoundaryTimestamp &&
        (endsAtTimestamp === null || endsAtTimestamp > endingBoundaryTimestamp)
      )
    })
  )
}
